import { useCallback, useEffect, useMemo, useRef } from 'react'
import { type NodeApi, Tree, type TreeApi } from 'react-arborist'
import type { TreeItem } from '@/types'
import { findBySlugWithPath, getStoriesTree } from '../../utils/parsedTree.js'
import { setTreeApi } from '../atoms/treeState.js'
import { useSelectedNodes } from '../hooks/useSelectedNodes.js'
import { useStoryRoute } from '../hooks/useStoryRoute.js'
import { NavigationTreeNode } from './NavigationTreeNode/NavigationTreeNode.js'

type Props = {
  height: number
}

export function NavigationTree(props: Props) {
  const { height: treeHeight } = props
  const { story, filter } = useStoryRoute()

  // This ref is required to limit the DnD api of React Arborist to the tree root
  const rTreeRoot = useRef<HTMLDivElement>(null)

  const treeApi = useRef<TreeApi<TreeItem> | undefined>(undefined)

  // store the tree API when it is available
  const api = treeApi.current
  useEffect(() => setTreeApi(api), [api])

  const stories = useMemo<TreeItem[]>(() => {
    if (filter) {
      const foundNode = findBySlugWithPath(filter)

      if (foundNode) {
        const { item, path } = foundNode

        // overwrite label w/ folder path
        const title = path.map(x => x.title).join('/')
        const temporaryTreeRoot: TreeItem = { ...item, title }

        return [temporaryTreeRoot]
      }
    }

    return Object.values(getStoriesTree())
  }, [filter])

  const { activeStory, onSelectStory } = useSelectedNodes()

  const handleOnSelect = useCallback(
    (nodes: NodeApi<TreeItem>[]) => {
      const node = nodes[0]
      if (!node) return

      if (node.isLeaf) {
        onSelectStory(node.data.slug)
      }
    },
    [onSelectStory],
  )

  // ATTENTION: the Tree component cannot be mounted until a ref is available, otherwise the DnD root is set to the entire `window` object
  const treeRootReady = rTreeRoot.current

  return (
    <div ref={rTreeRoot}>
      {treeRootReady ? (
        <Tree
          data={stories}
          disableDrag
          disableDrop
          disableEdit
          disableMultiSelection
          dndRootElement={rTreeRoot.current}
          height={treeHeight}
          idAccessor="slug"
          indent={12}
          onSelect={handleOnSelect}
          openByDefault={false}
          paddingBottom={100}
          ref={treeApi}
          rowHeight={34}
          selection={activeStory}
          width="100%"
        >
          {NavigationTreeNode}
        </Tree>
      ) : null}
    </div>
  )
}
